"use strict";

/**
 * @typedef {object} Logger
 * @property {Function} debug - Debug logging
 * @property {Function} info - Log info
 * @property {Function} warn - Log warnings
 * @property {Function} error - Log errors
 * @property {Function} log - Vanilla logging
 */
const logLevels = ["debug", "info", "warn", "error", "log"];

/**
 * Check if global variable is true
 * @param {string} flag - Variable flag
 * @returns {boolean} truthful flag
 */
export const checkGlobalVariableFlag = (flag) => {
  return global && global[flag] === true;
};

/**
 * `createLogger`
 * Create logger instance
 * @param {object} config - Logger config
 * @param {string} [config.variableFlag=debugMode] - Global variable flag for enabling the logger
 * @param {string} [config.name=Default] - Logger name
 *
 * @example
 * ```js
 * import { createLogger } from "@/utilities/logger";
 *
 * const filesMSLogger = createLogger({ variableFlag: "debugFilesMS", logPrefix: "Files" });
 *
 * filesMSLogger.info("fooBar", { foo: "bar" });
 *
 * // Result:
 * [Files] fooBar > {...}
 * ```
 * @returns {Logger} logger
 */
export const createLogger = (config) => {
  const globalDebug = () => checkGlobalVariableFlag("sleekDebug");
  const { variableFlag = "debugMode", name = "Default" } = config;
  const isEnabled = () => checkGlobalVariableFlag(variableFlag);
  const prefix = `[${name}]`;

  const log = (transport, args) => {
    if (globalDebug() || isEnabled()) {
      transport(prefix, ...args);
    }
  };
  const logger = {};
  Object.values(logLevels).map((level) => {
    logger[level] = function () {
      log(console[level], [...arguments]);
    };
  });
  return logger;
};
